import { Link } from "@tanstack/react-router";
import { Bell, Heart, Plus, Search, User } from "lucide-react";
import { AppLogo } from "./AppLogo";
import { AppMenuDrawer } from "./AppMenuDrawer";
import { LanguageSelector } from "./LanguageSelector";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";

/** Profile page per role — never the dashboard. */
const PROFILE_HOME = {
  customer: "/customer/profile",
  partner: "/partner/profile",
  admin: "/admin/dashboard",
  super_admin: "/super-admin/dashboard",
} as const;

export function TopHeader() {
  const { user, hasRole } = useAuth();
  const t = useT();
  // Guests see no account shortcuts; non-customers go to their own console.
  const isCustomer = hasRole("customer");
  const profileTo = hasRole("customer")
    ? PROFILE_HOME.customer
    : hasRole("partner")
      ? PROFILE_HOME.partner
      : hasRole("super_admin")
        ? PROFILE_HOME.super_admin
        : hasRole("admin")
          ? PROFILE_HOME.admin
          : "/login";
  return (
    <header className="pointer-events-auto sticky top-0 z-30 flex items-center gap-2 border-b border-border/60 bg-background/85 px-3 py-2.5 backdrop-blur-md sm:px-4 sm:gap-3">
      <AppMenuDrawer />
      <Link to="/" className="shrink-0">
        <AppLogo />
      </Link>

      <div className="ml-2 hidden flex-1 md:block">
        <div className="relative max-w-xl">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            placeholder="Search location, city, area…"
            className="w-full rounded-full border border-border bg-secondary/60 py-2 pl-10 pr-4 text-sm outline-none transition focus:border-brand focus:bg-background focus:ring-2 focus:ring-brand/30"
          />
        </div>
      </div>
      <div className="ml-auto flex items-center gap-1.5">
        {!hasRole("partner") && (
          <Link
            to="/partner-register"
            className="hidden items-center gap-1.5 rounded-full bg-brand px-3.5 py-2 text-xs font-semibold text-brown-dark shadow-soft transition hover:brightness-105 md:inline-flex"
          >
            <Plus className="h-4 w-4" /> {t("btn.listProperty")}
          </Link>
        )}
        <LanguageSelector compact />
        {isCustomer && (
          <>
            <IconButton to="/customer/favourites" label={t("nav.favourites")}>
              <Heart className="h-4.5 w-4.5" />
            </IconButton>
            <IconButton to="/customer/notifications" label={t("nav.notifications")}>
              <Bell className="h-4.5 w-4.5" />
            </IconButton>
          </>
        )}
        {user ? (
          <Link
            to={profileTo}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-brown text-primary-foreground shadow-soft"
            aria-label={t("nav.profile")}
          >
            <User className="h-4 w-4" />
          </Link>
        ) : (
          <>
            <Link
              to="/login"
              className="rounded-full border border-border bg-background px-3.5 py-2 text-xs font-semibold text-brown-dark shadow-soft transition hover:bg-brand-soft"
            >
              Sign in
            </Link>
            <Link
              to="/register"
              className="hidden rounded-full bg-brown px-3.5 py-2 text-xs font-semibold text-primary-foreground shadow-soft transition hover:brightness-110 sm:inline-flex"
            >
              Register
            </Link>
          </>
        )}
      </div>
    </header>
  );
}

function IconButton({
  children,
  to,
  label,
}: {
  children: React.ReactNode;
  to: string;
  label: string;
}) {
  return (
    <Link
      to={to}
      aria-label={label}
      className="hidden h-9 w-9 items-center justify-center rounded-full bg-secondary/70 text-brown-dark transition hover:bg-brand-soft sm:flex"
    >
      {children}
    </Link>
  );
}
